import { createContext, createElement, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { PlanService } from '../services/plan.service';
import { IInstitutionPlan } from '../interfaces/plan.interface';

export interface PlanState {
    /** The selected institution's plan; null while loading, with no institution, or when the fetch failed. */
    plan: IInstitutionPlan | null;
    /** Fetch the plan again (after a seat was added or freed, or the plan was changed). */
    refresh: () => void;
}

/**
 * @param inert when true a provider above already supplies the answer, so this copy makes no network call.
 */
function useComputedPlan(inert = false): PlanState {
    const { selectedInstitutionId } = useAuth();
    const [plan, setPlan] = useState<IInstitutionPlan | null>(null);
    const [version, setVersion] = useState(0);

    useEffect(() => {
        if (inert) return;
        setPlan(null);
        if (!selectedInstitutionId) return;
        let alive = true;
        // Fail open to "no plan": the meter stays hidden and nothing is blocked client-side. The
        // server enforces the Free plan limits whatever the client shows.
        PlanService.getPlan(selectedInstitutionId)
            .then(p => { if (alive) setPlan(p); })
            .catch(() => { if (alive) setPlan(null); });
        return () => { alive = false; };
    }, [selectedInstitutionId, version, inert]);

    const refresh = useCallback(() => setVersion(v => v + 1), []);
    return { plan, refresh };
}

const PlanContext = createContext<PlanState | null>(null);

/**
 * Fetches the selected institution's plan once for everything below it, again when the institution
 * changes, and on `refresh()`. Mounted at the authenticated shell next to DashboardAccessProvider.
 *
 * Like `useDashboardAccess`, `usePlan` still works without the provider: it computes locally, so a
 * component (or a test) rendered on its own behaves the same.
 */
export function PlanProvider({ children }: { children: ReactNode }) {
    const value = useComputedPlan();
    return createElement(PlanContext.Provider, { value }, children);
}

export function usePlan(): PlanState {
    const shared = useContext(PlanContext);
    const local = useComputedPlan(!!shared);
    return shared ?? local;
}
